import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import { FiCalendar, FiChevronDown, FiChevronUp, FiRefreshCw, FiEdit3, FiSave } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { fetchAllStadiums } from '../actions/stadiumsActions';
import { fetchLeagueTeams, fetchAdminLeagueStructure } from '../actions/leaguesActions';
import { fetchAllReferees } from '../actions/judjesActions';
import { generateSchedule, generateScheduleFromFirstRound, validateSchedule } from '../utilities/scheduleGenerator';
import { isValidTime, toServerTime } from '../utilities/matchTime';
import { schedulePriority, canHaveGroupStages, NO_GROUP_STAGES_TEXT } from '../utilities/stagePriority';
import Loader from 'react-loader-spinner';
import moment from 'moment';
import '../styles/scheduleManagement.css';

function ScheduleManagement() {
  const history = useHistory();
  const dispatch = useDispatch();

  const stadiums = useSelector(state => state.stadium.stadiumsData);
  const referees = useSelector(state => state.judge.judgesData);

  // Данные чемпионатов/лиг/сезонов
  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);

  // Команды выбранной лиги
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Групповые данные (для league_type = 2)
  const [leagueType, setLeagueType] = useState(1);
  // Тип лиги по сезонам: { [league_id]: { [season_id]: 1 | 2 } }.
  // После переделки управления лигами тип живёт в настройках сезона
  // (league_settings), а leagues.league_type при создании всегда 1 —
  // поэтому брать тип с самой лиги нельзя, групповые лиги считались обычными
  const [seasonTypes, setSeasonTypes] = useState({});
  // «Туров, идущих в группу» по сезонам: { [league_id]: { [season_id]: число } }
  const [seasonGroupStages, setSeasonGroupStages] = useState({});
  const [groupsData, setGroupsData] = useState({}); // { 'А': { teams: [...] }, 'Б': { ... } }
  const [generatedGroupSchedules, setGeneratedGroupSchedules] = useState(null); // { 'А': tours[], 'Б': tours[] }

  // Параметры генерации
  const [generationMode, setGenerationMode] = useState('auto'); // 'auto' или 'manual'
  const [firstRoundMatches, setFirstRoundMatches] = useState([]); // [{home: null, away: null}, ...]
  const [groupFirstRoundMatches, setGroupFirstRoundMatches] = useState({}); // { 'А': [{home, away}, ...], 'Б': [...] }
  const [firstRoundDate, setFirstRoundDate] = useState(moment().format('YYYY-MM-DD'));
  const [roundsCount, setRoundsCount] = useState(1);
  const [halfTimeDuration, setHalfTimeDuration] = useState(30);
  const [selectedStadium, setSelectedStadium] = useState(14);
  const [selectedReferee, setSelectedReferee] = useState(2);
  const [daysBetweenRounds, setDaysBetweenRounds] = useState(7);
  // Время матчей проставляется сразу при генерации: первый матч тура
  // начинается в firstMatchTime, каждый следующий сдвигается на интервал
  const [firstMatchTime, setFirstMatchTime] = useState('');
  const [minutesBetweenMatches, setMinutesBetweenMatches] = useState(90);

  // Результат генерации
  const [generatedSchedule, setGeneratedSchedule] = useState(null);

  // Правка даты и времени у конкретного матча уже сгенерированного расписания.
  // Генератор расставляет всё по шаблону, но отдельные матчи почти всегда
  // приходится двигать — перенос, занятое поле, вечерний слот
  const updateMatchField = (tourNumber, matchId, field, value, groupName) => {
    const patchTours = (tours) => tours.map(tour => (
      tour.tour_number !== tourNumber ? tour : {
        ...tour,
        matches: tour.matches.map(match => (
          match.id === matchId ? { ...match, [field]: value || null } : match
        )),
      }
    ));

    if (groupName) {
      setGeneratedGroupSchedules(prev => (prev ? {
        ...prev,
        [groupName]: patchTours(prev[groupName] || []),
      } : prev));
      return;
    }
    setGeneratedSchedule(prev => (prev ? patchTours(prev) : prev));
  };
  const [validation, setValidation] = useState(null);
  const [expandedTours, setExpandedTours] = useState({});

  // Загрузка
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadChampionships();
    dispatch(fetchAllStadiums(API_URLS.getAllStadiums));
    dispatch(fetchAllReferees(API_URLS.getAllReferees));
  }, []);

  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      loadLeagueTeams();
    }
    // seasonTypes в зависимостях: если справочник пришёл позже выбора,
    // перечитываем состав, чтобы подтянулись группы
  }, [selectedLeague, selectedSeason, seasonTypes]);

  // Справочник типов по сезонам из adminLeagueStructure — там тип сезона
  // есть надёжно, в отличие от /leagues/1/adminLeagues
  useEffect(() => {
    let cancelled = false;
    dispatch(fetchAdminLeagueStructure())
      .then(payload => {
        if (cancelled || !payload) return;
        const map = {};
        const groupStagesMap = {};
        const collect = (league) => {
          const leagueId = Number(league.id !== undefined ? league.id : league.league_id);
          (league.seasons || []).forEach(season => {
            const settings = season.settings || season;
            const raw = season.settings && season.settings.league_type !== undefined
              ? season.settings.league_type
              : season.league_type;
            if (!map[leagueId]) map[leagueId] = {};
            map[leagueId][Number(season.season_id)] = Number(raw) === 2 ? 2 : 1;
            if (!groupStagesMap[leagueId]) groupStagesMap[leagueId] = {};
            groupStagesMap[leagueId][Number(season.season_id)] = Number(settings.group_stages_amount) || 0;
          });
        };
        (payload.championats || []).forEach(ch => (ch.leagues || []).forEach(collect));
        (payload.orphan_leagues || []).forEach(collect);
        setSeasonTypes(map);
        setSeasonGroupStages(groupStagesMap);
      })
      .catch(e => console.error('Не удалось загрузить типы сезонов:', e));
    return () => { cancelled = true; };
  }, [dispatch]);

  // Тип выбранного сезона: сначала сам объект сезона, затем справочник,
  // и только в последнюю очередь поле лиги
  const resolveLeagueType = (league, season) => {
    if (season) {
      const own = season.settings && season.settings.league_type !== undefined
        ? season.settings.league_type
        : season.league_type;
      if (own !== undefined && own !== null) return Number(own) === 2 ? 2 : 1;
      const leagueId = league ? Number(league.value !== undefined ? league.value : league.league_id) : null;
      const bySeason = leagueId !== null && seasonTypes[leagueId]
        ? seasonTypes[leagueId][Number(season.value !== undefined ? season.value : season.season_id)]
        : undefined;
      if (bySeason) return bySeason;
    }
    return league && Number(league.league_type) === 2 ? 2 : 1;
  };

  // Определяем league_type при выборе лиги и сезона
  useEffect(() => {
    setLeagueType(selectedLeague ? resolveLeagueType(selectedLeague, selectedSeason) : 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeague, selectedSeason, seasonTypes]);

  // Инициализируем слоты первого тура при изменении команд
  // Для нечётного числа команд: одна пропускает, поэтому (N-1)/2 матчей
  useEffect(() => {
    if (teams.length >= 2) {
      const numMatches = Math.floor(teams.length / 2);
      const emptyMatches = Array.from({ length: numMatches }, () => ({ home: null, away: null }));
      setFirstRoundMatches(emptyMatches);
    }
  }, [teams]);

  // Инициализируем слоты первого тура для каждой группы
  useEffect(() => {
    if (leagueType === 2 && Object.keys(groupsData).length > 0) {
      const groupMatches = {};
      Object.entries(groupsData).forEach(([groupName, group]) => {
        if (group.teams.length >= 2) {
          const numMatches = Math.floor(group.teams.length / 2);
          groupMatches[groupName] = Array.from({ length: numMatches }, () => ({ home: null, away: null }));
        }
      });
      setGroupFirstRoundMatches(groupMatches);
    }
  }, [groupsData, leagueType]);

  const loadChampionships = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const response = await axios.post(
        BASE_URL.url + '/leagues/1/adminLeagues',
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const leaguesData = Array.isArray(response.data) ? response.data : response.data.data;

      if (leaguesData && leaguesData.length > 0) {
        const championsMap = {};

        leaguesData.forEach(item => {
          const championatName = item.championat_name || 'Без чемпионата';

          if (!championsMap[championatName]) {
            championsMap[championatName] = {
              value: championatName,
              name: championatName,
              leagues: []
            };
          }

          if (item.league_id > 0) {
            championsMap[championatName].leagues.push({
              value: item.league_id,
              name: item.league_name,
              seasons: item.seasons || [],
              ...item
            });
          }
        });

        const formattedChampionships = Object.values(championsMap)
          .filter(c => c.leagues.length > 0)
          .map((item, index) => ({
            ...item,
            key: index
          }));

        setChampionships(formattedChampionships);
      }
    } catch (error) {
      console.error('Error loading championships:', error);
    }
  };

  // Подстраховка от дублей: одна и та же команда не должна попасть
  // в расписание дважды, каким бы кривым ни оказался ответ
  const dedupeTeams = (list) => {
    const seen = new Set();
    const result = [];
    (list || []).forEach(team => {
      const id = Number(team.team_id !== undefined ? team.team_id : team.id);
      if (!id || seen.has(id)) return;
      seen.add(id);
      result.push({
        ...team,
        team_id: id,
        team_name: team.team_name || team.name,
        name: team.name || team.team_name,
      });
    });
    return result;
  };

  const loadLeagueTeams = async () => {
    if (!selectedLeague || !selectedSeason) return;

    setLoadingTeams(true);
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      // Состав сезона берём из related_leagues (/leagues/:id/teams).
      // Раньше тут был /teams/getAdminTeams — он игнорирует тело запроса,
      // отдаёт все команды клиента и склеивает teams.client_owner с
      // teams_owners. Из-за этого команда, перенесённая из чужой лиги и
      // добавленная клиенту в teams_owners, приходила дважды и задваивалась
      // в расписании. К составу сезона teams_owners отношения не имеет.
      const rawTeams = await dispatch(
        fetchLeagueTeams(selectedLeague.value, selectedSeason.value)
      );
      const teamsData = dedupeTeams(
        Array.isArray(rawTeams) ? rawTeams : (rawTeams && rawTeams.data) || []
      );
      setTeams(teamsData);

      // Для лиг с группами (league_type = 2) загружаем команды по группам
      const type = resolveLeagueType(selectedLeague, selectedSeason);
      if (type === 2) {
        try {
          const groupsUrl = BASE_URL.url + API_URLS.getTeamsByGroups.replace(':leagueId', selectedLeague.value);
          const groupsResponse = await axios.post(
            groupsUrl,
            { season_id: selectedSeason.value },
            {
              headers: {
                Authorization: "Bearer " + tokens.access_token,
                Accept: 'application/json',
                'Content-Type': 'application/json',
              }
            }
          );
          const groupTeams = dedupeTeams(
            Array.isArray(groupsResponse.data) ? groupsResponse.data : []
          );
          // Группируем по group_name
          const grouped = {};
          groupTeams.forEach(t => {
            const gName = t.group_name || 'Без группы';
            if (!grouped[gName]) {
              grouped[gName] = { teams: [] };
            }
            grouped[gName].teams.push(t);
          });
          setGroupsData(grouped);
        } catch (groupErr) {
          console.error('Error loading groups:', groupErr);
          setGroupsData({});
        }
      } else {
        setGroupsData({});
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleChampionshipChange = (championship_id) => {
    const championship = championships.find(c => c.value === championship_id);
    setSelectedChampionship(championship);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setTeams([]);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
    setGroupsData({});

    if (championship && championship.leagues && championship.leagues.length > 0) {
      setLeagues(championship.leagues);
    } else {
      setLeagues([]);
    }
  };

  const handleLeagueChange = (league_id) => {
    const league = leagues.find(l => l.value === league_id);
    setSelectedLeague(league);
    setSelectedSeason(null);
    setTeams([]);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
    setGroupsData({});

    if (league && league.seasons && league.seasons.length > 0) {
      const formattedSeasons = league.seasons.map((item, index) => ({
        value: item.season_id,
        name: item.season_name,
        key: index,
        ...item
      }));
      setSeasons(formattedSeasons);
    } else {
      setSeasons([]);
    }
  };

  const handleSeasonChange = (season_id) => {
    const season = seasons.find(s => s.value === season_id);
    setSelectedSeason(season);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
  };

  // --- Первый тур (ручной режим) ---
  const getUsedTeamIds = (excludeIndex = -1) => {
    const ids = new Set();
    firstRoundMatches.forEach((match, idx) => {
      if (idx === excludeIndex) return;
      if (match.home) ids.add(match.home.team_id || match.home.id);
      if (match.away) ids.add(match.away.team_id || match.away.id);
    });
    return ids;
  };

  const getAvailableTeamsForSlot = (matchIndex, side) => {
    const usedIds = getUsedTeamIds(matchIndex);
    // Убираем команду, уже выбранную на другой стороне этого же матча
    const currentMatch = firstRoundMatches[matchIndex];
    const otherSide = side === 'home' ? currentMatch.away : currentMatch.home;
    if (otherSide) {
      usedIds.add(otherSide.team_id || otherSide.id);
    }
    // Не убираем текущий выбор этой стороны (чтобы он отображался в списке)
    const currentTeam = side === 'home' ? currentMatch.home : currentMatch.away;
    if (currentTeam) {
      usedIds.delete(currentTeam.team_id || currentTeam.id);
    }

    return teams.filter(t => !usedIds.has(t.team_id || t.id));
  };

  const handleFirstRoundTeamChange = (matchIndex, side, teamId) => {
    const team = teams.find(t => (t.team_id || t.id) === teamId) || null;
    setFirstRoundMatches(prev => {
      const updated = [...prev];
      updated[matchIndex] = { ...updated[matchIndex], [side]: team };
      return updated;
    });
    setGeneratedSchedule(null);
  };

  const isFirstRoundComplete = () => {
    return firstRoundMatches.length > 0 && firstRoundMatches.every(m => m.home && m.away);
  };

  const handleModeChange = (mode) => {
    setGenerationMode(mode);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
  };

  // --- Логика первого тура для групп ---
  const getGroupUsedTeamIds = (groupName, excludeIndex = -1) => {
    const ids = new Set();
    const matches = groupFirstRoundMatches[groupName] || [];
    matches.forEach((match, idx) => {
      if (idx === excludeIndex) return;
      if (match.home) ids.add(match.home.team_id || match.home.id);
      if (match.away) ids.add(match.away.team_id || match.away.id);
    });
    return ids;
  };

  const getGroupAvailableTeamsForSlot = (groupName, matchIndex, side) => {
    const groupTeams = groupsData[groupName]?.teams || [];
    const usedIds = getGroupUsedTeamIds(groupName, matchIndex);
    const matches = groupFirstRoundMatches[groupName] || [];
    const currentMatch = matches[matchIndex];
    if (currentMatch) {
      const otherSide = side === 'home' ? currentMatch.away : currentMatch.home;
      if (otherSide) usedIds.add(otherSide.team_id || otherSide.id);
      const currentTeam = side === 'home' ? currentMatch.home : currentMatch.away;
      if (currentTeam) usedIds.delete(currentTeam.team_id || currentTeam.id);
    }
    return groupTeams.filter(t => !usedIds.has(t.team_id || t.id));
  };

  const handleGroupFirstRoundTeamChange = (groupName, matchIndex, side, teamId) => {
    const groupTeams = groupsData[groupName]?.teams || [];
    const team = groupTeams.find(t => (t.team_id || t.id) === teamId) || null;
    setGroupFirstRoundMatches(prev => {
      const updated = { ...prev };
      const matches = [...(updated[groupName] || [])];
      matches[matchIndex] = { ...matches[matchIndex], [side]: team };
      updated[groupName] = matches;
      return updated;
    });
    setGeneratedGroupSchedules(null);
  };

  const isGroupFirstRoundComplete = () => {
    const groupNames = Object.keys(groupsData);
    if (groupNames.length === 0) return false;
    return groupNames.every(gName => {
      const matches = groupFirstRoundMatches[gName] || [];
      return matches.length > 0 && matches.every(m => m.home && m.away);
    });
  };

  const handleGenerate = () => {
    if (leagueType !== 2 && generationMode === 'manual' && !isFirstRoundComplete()) {
      alert('Заполните все матчи первого тура');
      return;
    }

    const stadiumsList = stadiums?.data || [];
    const currentStadium = stadiumsList.find(s => s.id === selectedStadium || s.stadium_id === selectedStadium);
    const currentReferee = refereesArray.find(r => r.id === selectedReferee || r.referee_id === selectedReferee);
    const refFullName = currentReferee
      ? `${(currentReferee.name || '').trim()} ${(currentReferee.family || '').trim()}`.trim()
      : '';

    const genOptions = {
      firstRoundDate,
      rounds: roundsCount,
      halfTimeDuration,
      stadiumId: selectedStadium,
      stadiumName: currentStadium?.name || currentStadium?.stadium_name || 'Не назначен',
      refereeId: selectedReferee,
      refereeName: refFullName,
      daysBetweenRounds,
      firstMatchTime,
      minutesBetweenMatches,
    };

    if (leagueType === 2) {
      // Генерация для групп
      const groupNames = Object.keys(groupsData);
      if (groupNames.length === 0) {
        alert('Группы не найдены для данной лиги');
        return;
      }
      if (generationMode === 'manual' && !isGroupFirstRoundComplete()) {
        alert('Заполните все матчи первого тура для каждой группы');
        return;
      }
      const groupSchedules = {};
      const expanded = {};
      groupNames.forEach(groupName => {
        const groupTeams = groupsData[groupName].teams;
        if (groupTeams.length < 2) return;
        let schedule;
        if (generationMode === 'manual') {
          schedule = generateScheduleFromFirstRound(groupFirstRoundMatches[groupName], genOptions, groupTeams);
        } else {
          schedule = generateSchedule(groupTeams, genOptions);
        }
        groupSchedules[groupName] = schedule;
        schedule.forEach(tour => {
          expanded[`${groupName}_${tour.tour_number}`] = true;
        });
      });
      setGeneratedGroupSchedules(groupSchedules);
      setGeneratedSchedule(null);
      setExpandedTours(expanded);
    } else {
      // Генерация для обычной лиги (league_type = 1)
      if (generationMode === 'auto' && (!teams || teams.length < 2)) {
        alert('В выбранной лиге должно быть минимум 2 команды');
        return;
      }

      let schedule;
      if (generationMode === 'manual') {
        schedule = generateScheduleFromFirstRound(firstRoundMatches, genOptions, teams);
      } else {
        schedule = generateSchedule(teams, genOptions);
      }

      const result = validateSchedule(schedule);
      setValidation(result);
      setGeneratedSchedule(schedule);
      setGeneratedGroupSchedules(null);

      const expanded = {};
      schedule.forEach(tour => {
        expanded[tour.tour_number] = true;
      });
      setExpandedTours(expanded);
    }
  };

  const toggleTour = (tourNumber) => {
    setExpandedTours(prev => ({
      ...prev,
      [tourNumber]: !prev[tourNumber]
    }));
  };

  const collapseAll = () => {
    setExpandedTours({});
  };

  const expandAll = () => {
    const expanded = {};
    if (generatedGroupSchedules) {
      Object.entries(generatedGroupSchedules).forEach(([groupName, tours]) => {
        tours.forEach(tour => {
          expanded[`${groupName}_${tour.tour_number}`] = true;
        });
      });
    } else if (generatedSchedule) {
      generatedSchedule.forEach(tour => {
        expanded[tour.tour_number] = true;
      });
    }
    setExpandedTours(expanded);
  };

  // Стадии сезона на сервере: туры расписания получают priority 1..N,
  // и если в сезоне уже есть стадии, номера совпадут
  const loadSeasonStages = async () => {
    if (checkIfTokenExpired()) {
      await dispatch(refreshTokens());
    }
    const tokens = getTokensFromStore();
    const response = await axios.post(
      BASE_URL.url + `/leagues/${selectedLeague.value}/getLeagueStages`,
      { league_id: selectedLeague.value, season_id: selectedSeason.value },
      {
        headers: {
          Authorization: "Bearer " + tokens.access_token,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }
    );
    return Array.isArray(response.data) ? response.data : (response.data.data || []);
  };

  const handleSaveSchedule = async () => {
    const hasSchedule = generatedSchedule || generatedGroupSchedules;
    if (!hasSchedule || !selectedLeague || !selectedSeason || saving) return;

    const allTours = generatedGroupSchedules
      ? Object.keys(generatedGroupSchedules).reduce((acc, key) => acc.concat(generatedGroupSchedules[key]), [])
      : generatedSchedule;
    const badTimes = allTours.reduce(
      (count, tour) => count + tour.matches.filter(match => !isValidTime(match.time)).length, 0
    );
    if (badTimes > 0) {
      alert(`Проверьте время у матчей (${badTimes}): нужен формат ЧЧ:ММ`);
      return;
    }

    // Формат сезона для номеров туров: групповые туры — не дальше «Туров в группе»
    const leagueIdNum = Number(selectedLeague.value);
    const seasonIdNum = Number(selectedSeason.value);
    const scheduleFormat = {
      leagueType,
      groupStagesAmount: (seasonGroupStages[leagueIdNum] && seasonGroupStages[leagueIdNum][seasonIdNum]) || 0,
    };
    if (leagueType === 2 && Object.keys(seasonGroupStages).length === 0) {
      alert('Настройки сезона ещё загружаются — попробуйте через пару секунд.');
      return;
    }
    if (generatedGroupSchedules && !canHaveGroupStages(scheduleFormat)) {
      alert(NO_GROUP_STAGES_TEXT);
      return;
    }

    const totalTours = getTotalTours();
    const totalMatches = getTotalMatches();
    const groupInfo = generatedGroupSchedules
      ? ` (${Object.keys(generatedGroupSchedules).length} групп)`
      : '';

    let existingStages;
    try {
      existingStages = await loadSeasonStages();
    } catch (error) {
      console.error('Error loading season stages:', error);
      alert('Не удалось проверить, есть ли в сезоне туры. Попробуйте ещё раз.');
      return;
    }
    // Сервер не сохраняет расписание поверх существующих туров (409) —
    // говорим об этом сразу, а не после подтверждения
    if (existingStages.length > 0) {
      alert(
        `В сезоне уже есть стадий: ${existingStages.length}. Расписание сохраняется только в пустой сезон.\n\n`
        + 'Если расписание уже сохраняли, удалите старые туры на странице стадий '
        + 'или создавайте туры и матчи вручную.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Сохранить расписание?\n\nБудет создано ${totalTours} стадий (туров)${groupInfo} и ${totalMatches} матчей в лиге "${selectedLeague.name}".`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      if (generatedGroupSchedules) {
        // Сохранение для групп — объединяем все группы в единые туры
        const groupNames = Object.keys(generatedGroupSchedules);
        // Определяем максимальное количество туров среди всех групп
        const maxTours = Math.max(...groupNames.map(g => generatedGroupSchedules[g].length));

        const stages = [];
        for (let i = 1; i <= maxTours; i++) {
          stages.push({
            stage_name: i + ' Тур',
            short_stage_name: i + ' Тур',
            // Все туры групп — групповой этап, даже если их больше «Туров в группе»
            priority: schedulePriority(i, scheduleFormat),
          });
        }

        // Собираем матчи всех групп, привязывая к единым турам
        const matches = [];
        for (const groupName of groupNames) {
          const tours = generatedGroupSchedules[groupName];
          tours.forEach(tour => {
            tour.matches.forEach(match => {
              matches.push({
                home_team_id: match.home_team.team_id || match.home_team.id,
                guest_team_id: match.away_team.team_id || match.away_team.id,
                date: match.date,
                time: toServerTime(match.time),
                stadium_id: match.stadium_id,
                judje_id: match.referee_id || 2,
                half_time: match.half_time_duration,
                tour_number: match.tour_number,
              });
            });
          });
        }

        const response = await axios.post(
          BASE_URL.url + API_URLS.saveSchedule,
          {
            league_id: selectedLeague.value,
            season_id: selectedSeason.value,
            stages,
            matches,
            group_name: groupNames.join(','),
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data && response.data.answer) {
          alert('Расписание сохранено!');
          setGeneratedSchedule(null);
          setGeneratedGroupSchedules(null);
        } else {
          alert('Ошибка при сохранении: ' + (response.data?.error || 'Неизвестная ошибка'));
        }
      } else {
        // Сохранение для обычной лиги
        const stages = generatedSchedule.map(tour => ({
          stage_name: tour.tour_number + ' Тур',
          short_stage_name: tour.tour_number + ' Тур',
          priority: schedulePriority(tour.tour_number, scheduleFormat),
        }));

        const matches = generatedSchedule.flatMap(tour =>
          tour.matches.map(match => ({
            home_team_id: match.home_team.team_id || match.home_team.id,
            guest_team_id: match.away_team.team_id || match.away_team.id,
            date: match.date,
            time: toServerTime(match.time),
            stadium_id: match.stadium_id,
            judje_id: match.referee_id || 2,
            half_time: match.half_time_duration,
            tour_number: match.tour_number,
          }))
        );

        const response = await axios.post(
          BASE_URL.url + API_URLS.saveSchedule,
          {
            league_id: selectedLeague.value,
            season_id: selectedSeason.value,
            stages,
            matches,
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data && response.data.answer) {
          alert('Расписание успешно сохранено!');
          setGeneratedSchedule(null);
          setGeneratedGroupSchedules(null);
        } else {
          alert('Ошибка при сохранении: ' + (response.data?.error || 'Неизвестная ошибка'));
        }
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      if (error.response?.status === 409) {
        const errData = error.response?.data;
        const errMsg = errData?.error || errData?.message
          || (typeof errData === 'string' ? errData : null)
          || 'У сезона уже есть туры. Сначала удалите существующие стадии или создавайте туры вручную.';
        alert(errMsg);
      } else {
        const errData = error.response?.data;
        const errMsg = errData?.error || errData?.message
          || (typeof errData === 'string' ? errData : error.message);
        alert('Ошибка при сохранении: ' + errMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map(item => item.item);
    };
  };

  // Формируем опции стадионов
  const stadiumOptions = (stadiums?.data || []).map(s => ({
    value: s.id || s.stadium_id,
    name: s.name || s.stadium_name || `Стадион #${s.id || s.stadium_id}`,
  }));

  // Формируем опции судей
  const refereesArray = referees?.data || (Array.isArray(referees) ? referees : []);
  const refereeOptions = refereesArray.map(r => {
    const familyName = r.family ? r.family.trim() : '';
    const firstName = r.name ? r.name.trim() : '';
    const fullName = familyName ? `${firstName} ${familyName}` : firstName;
    return {
      value: r.id || r.referee_id,
      name: fullName || `Судья #${r.id || r.referee_id}`,
    };
  });

  const getTotalMatches = () => {
    if (generatedGroupSchedules) {
      return Object.values(generatedGroupSchedules).reduce((sum, tours) =>
        sum + tours.reduce((s, tour) => s + tour.matches.length, 0), 0);
    }
    if (!generatedSchedule) return 0;
    return generatedSchedule.reduce((sum, tour) => sum + tour.matches.length, 0);
  };

  const getTotalTours = () => {
    if (generatedGroupSchedules) {
      return Math.max(...Object.values(generatedGroupSchedules).map(tours => tours.length), 0);
    }
    if (!generatedSchedule) return 0;
    return generatedSchedule.length;
  };

  return (
    <div className="layoutWrapper">
      <div className="schedule-management-container">
        <div className="schedule-header">
          <h1><FiCalendar /> Управление расписанием</h1>
        </div>

        {/* Выбор лиги */}
        <div className="schedule-section">
          <h2 className="section-title">Выбор лиги</h2>
          <div className="schedule-filters">
            <div className="filter-group">
              <label>Чемпионат</label>
              <SelectSearch
                options={championships}
                search
                value={selectedChampionship?.value}
                onChange={handleChampionshipChange}
                filterOptions={fuzzySearch}
                emptyMessage="Чемпионат не найден"
                placeholder="Выберите чемпионат"
              />
            </div>

            {selectedChampionship && leagues.length > 0 && (
              <div className="filter-group">
                <label>Лига</label>
                <SelectSearch
                  options={leagues}
                  search
                  value={selectedLeague?.value}
                  onChange={handleLeagueChange}
                  filterOptions={fuzzySearch}
                  emptyMessage="Лига не найдена"
                  placeholder="Выберите лигу"
                />
              </div>
            )}

            {selectedLeague && seasons.length > 0 && (
              <div className="filter-group">
                <label>Сезон</label>
                <SelectSearch
                  options={seasons}
                  search
                  value={selectedSeason?.value}
                  onChange={handleSeasonChange}
                  filterOptions={fuzzySearch}
                  emptyMessage="Сезон не найден"
                  placeholder="Выберите сезон"
                />
              </div>
            )}
          </div>

          {/* Инфо о командах */}
          {selectedLeague && selectedSeason && (
            <div className="teams-info">
              {loadingTeams ? (
                <div className="teams-loading">
                  <Loader type="Rings" color="#3182ce" height={30} width={30} />
                  <span>Загрузка команд...</span>
                </div>
              ) : leagueType === 2 && Object.keys(groupsData).length > 0 ? (
                <div className="teams-count">
                  <div>Тип лиги: <strong>Групповой этап + плей-офф</strong></div>
                  <div>Групп: <strong>{Object.keys(groupsData).length}</strong></div>
                  {Object.entries(groupsData).map(([groupName, group]) => (
                    <div key={groupName} className="group-teams-preview">
                      <span className="group-label">Группа {groupName}</span>
                      <span>({group.teams.length} команд): </span>
                      <span className="teams-list-preview">
                        {group.teams.map(t => t.team_name || t.name).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="teams-count">
                  Команд в лиге: <strong>{teams.length}</strong>
                  {teams.length > 0 && (
                    <span className="teams-list-preview">
                      {' '}({teams.map(t => t.team_name || t.name).join(', ')})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Параметры генерации */}
        {selectedLeague && selectedSeason && (leagueType === 2 ? Object.keys(groupsData).length > 0 : teams.length >= 2) && (
          <div className="schedule-section">
            <h2 className="section-title">Параметры генерации</h2>

            {/* Переключатель режима */}
            <div className="mode-toggle">
              <button
                className={`mode-btn ${generationMode === 'auto' ? 'mode-btn-active' : ''}`}
                onClick={() => handleModeChange('auto')}
              >
                Автоматически
              </button>
              <button
                className={`mode-btn ${generationMode === 'manual' ? 'mode-btn-active' : ''}`}
                onClick={() => handleModeChange('manual')}
              >
                <FiEdit3 /> Задать первый тур
              </button>
            </div>

            <div className="schedule-params">
              <div className="param-group">
                <label>Дата первого тура</label>
                <input
                  type="date"
                  value={firstRoundDate}
                  onChange={(e) => setFirstRoundDate(e.target.value)}
                  className="param-input"
                />
              </div>

              <div className="param-group">
                <label>Количество кругов</label>
                <select
                  value={roundsCount}
                  onChange={(e) => setRoundsCount(Number(e.target.value))}
                  className="param-input"
                >
                  <option value={1}>1 круг</option>
                  <option value={2}>2 круга</option>
                </select>
              </div>

              <div className="param-group">
                <label>Время тайма (мин)</label>
                <input
                  type="number"
                  value={halfTimeDuration}
                  onChange={(e) => setHalfTimeDuration(Number(e.target.value))}
                  min={10}
                  max={60}
                  className="param-input"
                />
              </div>

              <div className="param-group">
                <label>Дней между турами</label>
                <input
                  type="number"
                  value={daysBetweenRounds}
                  onChange={(e) => setDaysBetweenRounds(Number(e.target.value))}
                  min={1}
                  max={30}
                  className="param-input"
                />
              </div>

              <div className="param-group">
                <label>Время первого матча</label>
                <input
                  type="time"
                  value={firstMatchTime}
                  onChange={(e) => setFirstMatchTime(e.target.value)}
                  className="param-input"
                />
                <small className="param-hint">Пусто — время проставите вручную</small>
              </div>

              <div className="param-group">
                <label>Интервал между матчами (мин)</label>
                <input
                  type="number"
                  value={minutesBetweenMatches}
                  onChange={(e) => setMinutesBetweenMatches(Number(e.target.value))}
                  min={0}
                  max={600}
                  step={15}
                  disabled={!firstMatchTime}
                  className="param-input"
                />
                <small className="param-hint">0 — все матчи тура в одно время</small>
              </div>

              <div className="param-group">
                <label>Стадион</label>
                <SelectSearch
                  options={stadiumOptions}
                  search
                  value={selectedStadium}
                  onChange={(val) => setSelectedStadium(val)}
                  filterOptions={fuzzySearch}
                  emptyMessage="Стадион не найден"
                  placeholder="Выберите стадион"
                />
              </div>

              <div className="param-group">
                <label>Судья</label>
                <SelectSearch
                  options={refereeOptions}
                  search
                  value={selectedReferee}
                  onChange={(val) => setSelectedReferee(val)}
                  filterOptions={fuzzySearch}
                  emptyMessage="Судья не найден"
                  placeholder="Не назначен"
                />
              </div>
            </div>

            <div className="generate-actions">
              <button
                className="btn-generate"
                onClick={handleGenerate}
                disabled={generationMode === 'manual' && (leagueType === 2 ? !isGroupFirstRoundComplete() : !isFirstRoundComplete())}
              >
                <FiRefreshCw /> Сгенерировать расписание
              </button>
            </div>

            {/* Редактор первого тура для групп */}
            {leagueType === 2 && generationMode === 'manual' && Object.keys(groupsData).length > 0 && (
              <div className="first-round-editor">
                <h3 className="first-round-title">Матчи первого тура по группам</h3>
                {Object.entries(groupsData).map(([groupName, group]) => {
                  const matches = groupFirstRoundMatches[groupName] || [];
                  return (
                    <div key={groupName} className="group-first-round-block">
                      <h4 className="group-schedule-title">Группа {groupName}</h4>
                      <div className="first-round-matches">
                        {matches.map((match, idx) => {
                          const homeOptions = getGroupAvailableTeamsForSlot(groupName, idx, 'home').map(t => ({
                            value: t.team_id || t.id,
                            name: t.team_name || t.name,
                          }));
                          const awayOptions = getGroupAvailableTeamsForSlot(groupName, idx, 'away').map(t => ({
                            value: t.team_id || t.id,
                            name: t.team_name || t.name,
                          }));
                          return (
                            <div key={idx} className="first-round-match-row">
                              <div className="fr-match-number">{idx + 1}</div>
                              <div className="fr-match-team">
                                <SelectSearch
                                  options={homeOptions}
                                  search
                                  value={match.home ? (match.home.team_id || match.home.id) : ''}
                                  onChange={(val) => handleGroupFirstRoundTeamChange(groupName, idx, 'home', val)}
                                  filterOptions={fuzzySearch}
                                  emptyMessage="Нет доступных"
                                  placeholder="Хозяева"
                                />
                              </div>
                              <span className="fr-vs">vs</span>
                              <div className="fr-match-team">
                                <SelectSearch
                                  options={awayOptions}
                                  search
                                  value={match.away ? (match.away.team_id || match.away.id) : ''}
                                  onChange={(val) => handleGroupFirstRoundTeamChange(groupName, idx, 'away', val)}
                                  filterOptions={fuzzySearch}
                                  emptyMessage="Нет доступных"
                                  placeholder="Гости"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Редактор первого тура (для обычных лиг) */}
            {leagueType !== 2 && generationMode === 'manual' && (
              <div className="first-round-editor">
                <h3 className="first-round-title">Матчи первого тура</h3>
                <p className="first-round-hint">
                  Укажите пары команд. Последний матч — хозяева будут «фиксированной» командой в алгоритме.
                  {teams.length % 2 !== 0 && ' Одна команда автоматически пропустит тур.'}
                </p>
                {teams.length % 2 !== 0 && isFirstRoundComplete() && (() => {
                  const usedIds = new Set();
                  firstRoundMatches.forEach(m => {
                    if (m.home) usedIds.add(m.home.team_id || m.home.id);
                    if (m.away) usedIds.add(m.away.team_id || m.away.id);
                  });
                  const byeTeam = teams.find(t => !usedIds.has(t.team_id || t.id));
                  return byeTeam ? (
                    <div className="bye-team-info">
                      Пропускает тур: <strong>{byeTeam.team_name || byeTeam.name}</strong>
                    </div>
                  ) : null;
                })()}
                <div className="first-round-matches">
                  {firstRoundMatches.map((match, idx) => {
                    const homeOptions = getAvailableTeamsForSlot(idx, 'home').map(t => ({
                      value: t.team_id || t.id,
                      name: t.team_name || t.name,
                    }));
                    const awayOptions = getAvailableTeamsForSlot(idx, 'away').map(t => ({
                      value: t.team_id || t.id,
                      name: t.team_name || t.name,
                    }));

                    return (
                      <div key={idx} className="first-round-match-row">
                        <div className="fr-match-number">{idx + 1}</div>
                        <div className="fr-match-team">
                          <SelectSearch
                            options={homeOptions}
                            search
                            value={match.home ? (match.home.team_id || match.home.id) : ''}
                            onChange={(val) => handleFirstRoundTeamChange(idx, 'home', val)}
                            filterOptions={fuzzySearch}
                            emptyMessage="Нет доступных"
                            placeholder="Хозяева"
                          />
                        </div>
                        <span className="fr-vs">vs</span>
                        <div className="fr-match-team">
                          <SelectSearch
                            options={awayOptions}
                            search
                            value={match.away ? (match.away.team_id || match.away.id) : ''}
                            onChange={(val) => handleFirstRoundTeamChange(idx, 'away', val)}
                            filterOptions={fuzzySearch}
                            emptyMessage="Нет доступных"
                            placeholder="Гости"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!isFirstRoundComplete() && (
                  <p className="first-round-warning">Заполните все матчи для генерации расписания</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Предварительный просмотр */}
        {(generatedSchedule || generatedGroupSchedules) && (
          <div className="schedule-section">
            <div className="preview-header">
              <h2 className="section-title">Предварительный просмотр расписания</h2>
              <div className="preview-stats">
                <span className="stat-item">Туров: <strong>{getTotalTours()}</strong></span>
                <span className="stat-item">Матчей: <strong>{getTotalMatches()}</strong></span>
                <span className="stat-item">Команд: <strong>{teams.length}</strong></span>
                {generatedGroupSchedules && (
                  <span className="stat-item">Групп: <strong>{Object.keys(generatedGroupSchedules).length}</strong></span>
                )}
              </div>
            </div>

            <div className="preview-actions">
              <button className="btn-small" onClick={expandAll}>Развернуть все</button>
              <button className="btn-small" onClick={() => setExpandedTours({})}>Свернуть все</button>
              <button
                className="btn-save-schedule"
                onClick={handleSaveSchedule}
                disabled={saving}
              >
                <FiSave /> {saving ? 'Сохранение...' : 'Сохранить на сервер'}
              </button>
            </div>

            {/* Расписание по группам */}
            {generatedGroupSchedules && Object.entries(generatedGroupSchedules).map(([groupName, tours]) => (
              <div key={groupName} className="group-schedule-block">
                <h3 className="group-schedule-title">Группа {groupName}</h3>
                <div className="tours-list">
                  {tours.map(tour => {
                    const tourKey = `${groupName}_${tour.tour_number}`;
                    return (
                      <div key={tourKey} className="tour-card">
                        <div className="tour-header" onClick={() => setExpandedTours(prev => ({ ...prev, [tourKey]: !prev[tourKey] }))}>
                          <div className="tour-title">
                            <span className="tour-number">Тур {tour.tour_number}</span>
                            {roundsCount > 1 && <span className="tour-circle">Круг {tour.circle}</span>}
                            <span className="tour-date">{moment(tour.date).format('DD.MM.YYYY')}</span>
                            <span className="tour-matches-count">{tour.matches.length} матч(ей)</span>
                          </div>
                          {expandedTours[tourKey] ? <FiChevronUp /> : <FiChevronDown />}
                        </div>

                        {expandedTours[tourKey] && (
                          <div className="tour-matches">
                            {tour.matches.map((match, idx) => (
                              <div key={match.id} className="match-row">
                                <div className="match-number">{idx + 1}</div>
                                <div className="match-teams">
                                  <span className="team-home">{match.home_team.team_name || match.home_team.name}</span>
                                  <span className="match-vs">vs</span>
                                  <span className="team-away">{match.away_team.team_name || match.away_team.name}</span>
                                </div>
                                <div className="match-info">
                                  <input
                                    type="date"
                                    className="match-date-input"
                                    value={match.date || ''}
                                    onChange={(e) => updateMatchField(
                                      tour.tour_number, match.id, 'date', e.target.value, groupName,
                                    )}
                                  />
                                  <input
                                    type="time"
                                    className="match-time-input"
                                    value={match.time || ''}
                                    onChange={(e) => updateMatchField(
                                      tour.tour_number, match.id, 'time', e.target.value, groupName,
                                    )}
                                  />
                                  <span className="match-stadium">{match.stadium_name}</span>
                                  {match.referee_name && <span className="match-referee">{match.referee_name}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Расписание обычной лиги */}
            {generatedSchedule && (
            <div className="tours-list">
              {generatedSchedule.map(tour => (
                <div key={tour.tour_number} className="tour-card">
                  <div className="tour-header" onClick={() => toggleTour(tour.tour_number)}>
                    <div className="tour-title">
                      <span className="tour-number">Тур {tour.tour_number}</span>
                      {roundsCount > 1 && <span className="tour-circle">Круг {tour.circle}</span>}
                      <span className="tour-date">{moment(tour.date).format('DD.MM.YYYY')}</span>
                      <span className="tour-matches-count">{tour.matches.length} матч(ей)</span>
                    </div>
                    {expandedTours[tour.tour_number] ? <FiChevronUp /> : <FiChevronDown />}
                  </div>

                  {expandedTours[tour.tour_number] && (
                    <div className="tour-matches">
                      {tour.matches.map((match, idx) => (
                        <div key={match.id} className="match-row">
                          <div className="match-number">{idx + 1}</div>
                          <div className="match-teams">
                            <span className="team-home">{match.home_team.team_name || match.home_team.name}</span>
                            <span className="match-vs">vs</span>
                            <span className="team-away">{match.away_team.team_name || match.away_team.name}</span>
                          </div>
                          <div className="match-info">
                            <input
                              type="date"
                              className="match-date-input"
                              value={match.date || ''}
                              onChange={(e) => updateMatchField(
                                tour.tour_number, match.id, 'date', e.target.value,
                              )}
                            />
                            <input
                              type="time"
                              className="match-time-input"
                              value={match.time || ''}
                              onChange={(e) => updateMatchField(
                                tour.tour_number, match.id, 'time', e.target.value,
                              )}
                            />
                            <span className="match-stadium">{match.stadium_name}</span>
                            {match.referee_name && <span className="match-referee">{match.referee_name}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ScheduleManagement;
